import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { addDays, dayRange, today } from '../../lib/dates';
import { T, useGym } from '../../lib/gym';
import { useApi, useInterval } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, navigate, useLocation } from '../../lib/router';
import { attribution, type Plan, type ScheduleItem } from '../../lib/types';
import { AsyncButton, Button, Card, Checkbox, Empty, errorText, Field, fieldErrors, Input, Loading, Select, Textarea, useToast } from '../../ui/components';
import { Brand, LanguageSelect, TestModeBanner } from '../../ui/layout';
import { PlanPrice, ScheduleList } from '../../ui/lessons';
import { ChatBubble } from './chat';

export function PublicFrame({ children, embed }: { children: ReactNode; embed?: boolean }) {
  const gym = useGym();
  const { user } = useAuth();
  const i18n = useI18n();
  useEffect(() => {
    if (!embed || window.parent === window) return undefined;
    const post = (): void => window.parent.postMessage({ type: 'gymsuite:height', height: document.documentElement.scrollHeight }, '*');
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    post();
    return () => ro.disconnect();
  }, [embed]);
  if (embed) return <div className="embed">{children}</div>;
  const p = gym.gym.policies;
  return (
    <div className="public">
      <TestModeBanner />
      <header className="public-head">
        <Brand />
        <nav className="row gap-sm wrap">
          <Link to={`${gym.base}/schedule`}>{i18n.t('public.schedule')}</Link>
          <Link to={`${gym.base}/join`}>{i18n.t('public.memberships')}</Link>
          <LanguageSelect compact />
          {user && gym.membership
            ? <Button size="sm" onClick={() => navigate(`${gym.base}/planner`)}>{i18n.t('public.open_app')}</Button>
            : <Button size="sm" variant="secondary" onClick={() => navigate(`/login?gym=${gym.slug}&next=${encodeURIComponent(`${gym.base}/planner`)}`)}>{i18n.t('auth.login')}</Button>}
        </nav>
      </header>
      <main className="public-main">{children}</main>
      <footer className="public-foot">
        <span>© {new Date().getFullYear()} {gym.gym.name}</span>
        {gym.gym.supportEmail && <a href={`mailto:${gym.gym.supportEmail}`}>{gym.gym.supportEmail}</a>}
        {p.termsUrl && <a href={p.termsUrl} target="_blank" rel="noreferrer">{i18n.t('public.terms')}</a>}
        {p.privacyUrl && <a href={p.privacyUrl} target="_blank" rel="noreferrer">{i18n.t('public.privacy')}</a>}
      </footer>
      {gym.chatEnabled && <ChatBubble />}
    </div>
  );
}

function OpeningHours({ hours }: { hours?: Record<string, string[][]> }) {
  const i18n = useI18n();
  return (
    <table className="hours">
      <tbody>
        {[1, 2, 3, 4, 5, 6, 0].map((d) => (
          <tr key={d}>
            <th>{i18n.day(`2024-01-0${d === 0 ? 7 : d}`, { weekday: 'long' })}</th>
            <td>{(hours?.[String(d)] ?? []).map((r) => r.join('–')).join(', ') || i18n.t('public.closed')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function usePlans() {
  const gym = useGym();
  return useApi(() => api<Plan[]>(`/v1/public/${gym.slug}/plans`), [gym.slug]);
}

function PlanCard({ plan, onChoose, selected }: { plan: Plan; onChoose?: () => void; selected?: boolean }) {
  const i18n = useI18n();
  return (
    <div className={`plan ${selected ? 'selected' : ''}`}>
      <h3>{plan.name}</h3>
      <PlanPrice cents={plan.price_cents} interval={plan.billing_interval} count={plan.interval_count} />
      {plan.description && <p className="muted">{plan.description}</p>}
      <ul className="ticks">
        {plan.unlimited && <li>{i18n.t('plans.unlimited')}</li>}
        {plan.token_grants.map((g) => <li key={g.token_type_id}>{i18n.t(`plans.grant_${plan.grant_frequency ?? 'once'}`, { count: g.quantity, name: g.name ?? '' })}</li>)}
        <li>{plan.min_term_months ? i18n.t('plans.min_term', { months: plan.min_term_months }) : i18n.t('plans.no_min_term')}</li>
        {plan.billing_interval !== 'none' && <li>{i18n.t('plans.notice', { days: plan.notice_days ?? 30 })}</li>}
        {Boolean(plan.enrollment_fee_cents) && <li>{i18n.t('plans.enrollment_fee', { amount: i18n.money(plan.enrollment_fee_cents) })}</li>}
      </ul>
      {onChoose && <Button onClick={onChoose} variant={selected ? 'primary' : 'secondary'}>{i18n.t(selected ? 'plans.selected' : 'plans.choose')}</Button>}
    </div>
  );
}

function ContactForm() {
  const gym = useGym();
  const i18n = useI18n();
  const [v, setV] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '', website: '', marketing: false });
  const [sent, setSent] = useState(false);
  if (sent) return <p className="alert alert-ok">{i18n.t('public.contact_sent')}</p>;
  return (
    <form className="stack" onSubmit={(e) => e.preventDefault()}>
      <div className="grid-2">
        <Field label={i18n.field('first_name')}><Input required value={v.firstName} onChange={(e) => setV({ ...v, firstName: e.target.value })} /></Field>
        <Field label={i18n.field('last_name')}><Input value={v.lastName} onChange={(e) => setV({ ...v, lastName: e.target.value })} /></Field>
        <Field label={i18n.field('email')}><Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
        <Field label={i18n.field('phone')}><Input type="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
      </div>
      <Field label={i18n.t('public.message')}><Textarea value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} /></Field>
      <input className="hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={v.website} onChange={(e) => setV({ ...v, website: e.target.value })} name="website" />
      <Checkbox label={i18n.t('consent.marketing_email')} checked={v.marketing} onChange={(c) => setV({ ...v, marketing: c })} />
      <AsyncButton disabled={!v.firstName || (!v.email && !v.phone)} onClick={async () => {
        await api(`/v1/public/${gym.slug}/leads`, { body: { ...v, ...attribution(), locale: i18n.locale, consents: { marketingEmail: v.marketing } } });
        setSent(true);
      }}>{i18n.t('public.send')}</AsyncButton>
    </form>
  );
}

export function GymHome() {
  const gym = useGym();
  const i18n = useI18n();
  const plans = usePlans();
  const tz = gym.gym.timezone;
  const range = dayRange(today(tz), addDays(today(tz), 4), tz);
  const schedule = useApi(() => api<ScheduleItem[]>(`/v1/public/${gym.slug}/schedule`, { query: range }), [gym.slug]);
  const p = gym.gym.policies;
  return (
    <PublicFrame>
      <section className="hero">
        <h1>{gym.gym.name}</h1>
        <p>{gym.gym.branding?.heroText || i18n.t('public.hero')}</p>
        <div className="row gap-sm wrap center">
          {p.trial && <Button size="lg" onClick={() => navigate(`${gym.base}/trial`)}>{i18n.t('public.book_trial')}</Button>}
          <Button size="lg" variant="secondary" onClick={() => navigate(`${gym.base}/join`)}>{i18n.t('public.become_member')}</Button>
        </div>
      </section>
      <div className="public-grid">
        <Card title={i18n.t('public.next_lessons')} actions={<Link to={`${gym.base}/schedule`}>{i18n.t('public.full_schedule')}</Link>}>
          {schedule.data ? <ScheduleList items={schedule.data.slice(0, 12)} onPick={() => navigate(p.trial ? `${gym.base}/trial` : `${gym.base}/join`)} /> : <Loading />}
        </Card>
        <div className="stack">
          {gym.locations.map((l) => (
            <Card key={l.id} title={l.name}>
              <p className="muted">{[[l.street, l.house_number].filter(Boolean).join(' '), [l.postal_code, l.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
              <OpeningHours hours={l.opening_hours} />
              {gym.closures.filter((c) => c.location_id === l.id && c.closed).slice(0, 3).map((c) => (
                <p key={c.start_date} className="small warn">{i18n.t('public.closed_on', { from: i18n.day(c.start_date), to: i18n.day(c.end_date) })}{c.reason ? ` · ${c.reason}` : ''}</p>
              ))}
            </Card>
          ))}
        </div>
      </div>
      {plans.data && plans.data.length > 0 && (
        <section className="stack">
          <h2>{i18n.t('public.memberships')}</h2>
          <div className="plans">{plans.data.map((pl) => <PlanCard key={pl.id} plan={pl} onChoose={() => navigate(`${gym.base}/join?plan=${pl.id}`)} />)}</div>
        </section>
      )}
      <Card title={i18n.t('public.contact')}><ContactForm /></Card>
    </PublicFrame>
  );
}

export function PublicSchedule() {
  const gym = useGym();
  const i18n = useI18n();
  const tz = gym.gym.timezone;
  const [start, setStart] = useState(today(tz));
  const [loc, setLoc] = useState('');
  const range = dayRange(start, addDays(start, 7), tz);
  const schedule = useApi(() => api<ScheduleItem[]>(`/v1/public/${gym.slug}/schedule`, { query: { ...range, location: loc } }), [start, loc]);
  return (
    <PublicFrame embed={new URLSearchParams(window.location.search).get('embed') === '1'}>
      <div className="toolbar">
        <h1>{i18n.t('public.schedule')}</h1>
        <div className="row gap-sm">
          {gym.locations.length > 1 && <Select value={loc} onChange={(e) => setLoc(e.target.value)} placeholder={i18n.t('planner.all_locations')} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />}
          <Button variant="secondary" disabled={start <= today(tz)} onClick={() => setStart(addDays(start, -7))}>‹</Button>
          <Button variant="secondary" onClick={() => setStart(addDays(start, 7))}>›</Button>
        </div>
      </div>
      {schedule.data ? <ScheduleList items={schedule.data} onPick={() => navigate(gym.gym.policies.trial ? `${gym.base}/trial` : `${gym.base}/join`)} /> : <Loading />}
    </PublicFrame>
  );
}

const HEALTH_QUESTIONS = ['heart_condition', 'chest_pain', 'dizziness', 'joint_problems', 'medication', 'pregnant'];

export function TrialPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const embed = query.get('embed') === '1';
  const [loc, setLoc] = useState('');
  const [picked, setPicked] = useState<ScheduleItem | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [blocked, setBlocked] = useState('');
  const [v, setV] = useState({ firstName: '', lastName: '', email: '', phone: '', iceName: '', icePhone: '', notes: '', terms: false, marketingEmail: false, marketingSms: false, health: false });
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const sessions = useApi(() => api<ScheduleItem[]>(`/v1/public/${gym.slug}/trial-sessions`, { query: { location: loc } }), [loc]);
  const hasHealth = Object.values(answers).some(Boolean) || Boolean(v.notes.trim());
  const policies = gym.gym.policies;
  if (!policies.trial) return <PublicFrame embed={embed}><Empty>{i18n.t('trial.unavailable')}</Empty></PublicFrame>;
  const submit = async (): Promise<void> => {
    setErrors({});
    setBlocked('');
    try {
      const r = await api(`/v1/public/${gym.slug}/trial`, {
        body: {
          sessionId: picked!.id, firstName: v.firstName, lastName: v.lastName, email: v.email, phone: v.phone, locale: i18n.locale,
          consents: { terms: v.terms, privacy: v.terms, marketingEmail: v.marketingEmail, marketingSms: v.marketingSms, health: v.health },
          healthIntake: hasHealth && v.health ? answers : null, healthNotes: hasHealth && v.health ? v.notes : null,
          emergencyContact: v.iceName || v.icePhone ? { name: v.iceName, phone: v.icePhone, relation: '' } : null,
          ...attribution(),
        },
      });
      setDone(r.startsAt);
    } catch (e) {
      setErrors(fieldErrors(e));
      if (e instanceof ApiError && e.status === 409) setBlocked(errorText(e, i18n));
      else toast.error(e);
      if (e instanceof ApiError && e.code === 'trial_spots_full') { setPicked(null); sessions.reload(); }
    }
  };
  let body: ReactNode;
  if (done) {
    body = (
      <div className="stack center-text">
        <div className="success-mark" aria-hidden="true">✓</div>
        <h2>{i18n.t('trial.booked_title')}</h2>
        <p>{i18n.t('trial.booked_text', { lesson: picked?.lesson.name, when: i18n.dateTime(done), location: picked?.location.name })}</p>
        <p className="muted">{i18n.t('trial.bring')}</p>
        <p className="muted">{i18n.t('trial.email_sent')}</p>
      </div>
    );
  } else if (!picked) {
    body = (
      <div className="stack">
        <p className="muted">{i18n.t('trial.pick_intro')}</p>
        {gym.locations.length > 1 && <Select value={loc} onChange={(e) => setLoc(e.target.value)} placeholder={i18n.t('planner.all_locations')} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />}
        {sessions.data ? <ScheduleList items={sessions.data} onPick={setPicked} empty={i18n.t('trial.no_sessions')} right={() => <span className="btn btn-secondary btn-sm">{i18n.t('trial.choose')}</span>} /> : <Loading />}
      </div>
    );
  } else {
    body = (
      <form className="stack" onSubmit={(e) => e.preventDefault()}>
        <div className="picked-lesson">
          <div><strong>{picked.lesson.name}</strong><br /><span className="muted">{i18n.dateTime(picked.startsAt)} · {picked.location.name}</span></div>
          <Button variant="ghost" size="sm" onClick={() => setPicked(null)}>{i18n.t('trial.change')}</Button>
        </div>
        <div className="grid-2">
          <Field label={`${i18n.field('first_name')} *`} error={errors.firstName}><Input required autoComplete="given-name" value={v.firstName} onChange={(e) => setV({ ...v, firstName: e.target.value })} /></Field>
          <Field label={`${i18n.field('last_name')} *`}><Input required autoComplete="family-name" value={v.lastName} onChange={(e) => setV({ ...v, lastName: e.target.value })} /></Field>
          <Field label={`${i18n.field('email')} *`} error={errors.email}><Input type="email" required autoComplete="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>
          <Field label={`${i18n.t('trial.mobile')} *`} error={errors.phone} hint={i18n.t('trial.mobile_hint')}><Input type="tel" required autoComplete="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
        </div>
        <details className="details">
          <summary>{i18n.t('trial.ice_title')}</summary>
          <div className="grid-2">
            <Field label={i18n.field('name')}><Input value={v.iceName} onChange={(e) => setV({ ...v, iceName: e.target.value })} /></Field>
            <Field label={i18n.field('phone')}><Input type="tel" value={v.icePhone} onChange={(e) => setV({ ...v, icePhone: e.target.value })} /></Field>
          </div>
        </details>
        <details className="details">
          <summary>{i18n.t('trial.health_title')}</summary>
          <p className="small muted">{i18n.t('trial.health_intro')}</p>
          {HEALTH_QUESTIONS.map((q) => <Checkbox key={q} label={i18n.t(`health.q_${q}`)} checked={Boolean(answers[q])} onChange={(c) => setAnswers({ ...answers, [q]: c })} />)}
          <Field label={i18n.t('trial.health_notes')}><Textarea rows={2} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></Field>
          {hasHealth && <Checkbox label={i18n.t('consent.health')} checked={v.health} onChange={(c) => setV({ ...v, health: c })} />}
        </details>
        <Checkbox checked={v.terms} onChange={(c) => setV({ ...v, terms: c })} label={<ConsentText />} />
        <Checkbox label={i18n.t('consent.marketing_email')} checked={v.marketingEmail} onChange={(c) => setV({ ...v, marketingEmail: c })} />
        <Checkbox label={i18n.t('consent.marketing_sms')} checked={v.marketingSms} onChange={(c) => setV({ ...v, marketingSms: c })} />
        {blocked && <div className="alert alert-warn">{blocked} <Link to={`${gym.base}/join`}>{i18n.t('public.become_member')}</Link></div>}
        <AsyncButton size="lg" disabled={!v.terms || !v.firstName || !v.lastName || !v.email || !v.phone || (hasHealth && !v.health)} onClick={submit}>{i18n.t('trial.book')}</AsyncButton>
        <p className="small muted">{i18n.t('trial.once_only')}</p>
      </form>
    );
  }
  return (
    <PublicFrame embed={embed}>
      <div className="narrow stack">
        {!embed && <h1>{i18n.t('trial.title')}</h1>}
        {body}
      </div>
    </PublicFrame>
  );
}

function ConsentText() {
  const gym = useGym();
  const i18n = useI18n();
  const p = gym.gym.policies;
  return (
    <span>
      {i18n.t('consent.terms_prefix')}{' '}
      {p.termsUrl ? <a href={p.termsUrl} target="_blank" rel="noreferrer">{i18n.t('consent.terms')}</a> : i18n.t('consent.terms')}{' '}
      {i18n.t('consent.and')}{' '}
      {p.privacyUrl ? <a href={p.privacyUrl} target="_blank" rel="noreferrer">{i18n.t('consent.privacy')}</a> : i18n.t('consent.privacy')} *
    </span>
  );
}

export function JoinPage() {
  const gym = useGym();
  const i18n = useI18n();
  const { user, tenants, startSession, reload } = useAuth();
  const { query } = useLocation();
  const plans = usePlans();
  const [planId, setPlanId] = useState(query.get('plan') ?? '');
  const [early, setEarly] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameParts = (user?.name ?? '').split(' ');
  const [v, setV] = useState({ firstName: nameParts[0] ?? '', lastName: nameParts.slice(1).join(' '), email: '', password: '', phone: '', dateOfBirth: '', street: '', house_number: '', postal_code: '', city: '', terms: false, marketingEmail: false });
  const member = tenants.find((m) => m.slug === gym.slug);
  const plan = plans.data?.find((p) => p.id === planId) ?? null;
  const p = gym.gym.policies;
  const register = async (): Promise<void> => {
    setErrors({});
    try {
      const r = await api(`/v1/public/${gym.slug}/register`, {
        body: { ...v, email: user ? undefined : v.email, locale: i18n.locale, consents: { terms: v.terms, privacy: v.terms, marketingEmail: v.marketingEmail }, ...attribution() },
      });
      if (r.accessToken) await startSession(r);
      else await reload();
    } catch (e) {
      setErrors(fieldErrors(e));
      throw e;
    }
  };
  const checkout = async (): Promise<void> => {
    const r = await api(`/v1/public/${gym.slug}/signup`, { body: { planId, earlyStartConsent: early } });
    if (r.checkoutUrl) window.location.href = r.checkoutUrl;
    else navigate(`${gym.base}/welcome?membership=${r.membershipId}`);
  };
  if (!plans.data) return <PublicFrame><Loading /></PublicFrame>;
  return (
    <PublicFrame>
      <div className="stack">
        <h1>{i18n.t('join.title')}</h1>
        {query.get('cancelled') && <div className="alert alert-warn">{i18n.t('join.payment_cancelled')}</div>}
        {!gym.gym.paymentsEnabled && <div className="alert alert-warn">{i18n.t('join.payments_not_ready')}</div>}
        <div className="plans">{plans.data.map((pl) => <PlanCard key={pl.id} plan={pl} selected={pl.id === planId} onChoose={() => setPlanId(pl.id)} />)}</div>
        {plan && !member && (
          <Card title={user ? i18n.t('join.link_account', { email: user.email }) : i18n.t('join.your_details')}>
            <div className="grid-2">
              <Field label={`${i18n.field('first_name')} *`}><Input autoComplete="given-name" value={v.firstName} onChange={(e) => setV({ ...v, firstName: e.target.value })} /></Field>
              <Field label={`${i18n.field('last_name')} *`}><Input autoComplete="family-name" value={v.lastName} onChange={(e) => setV({ ...v, lastName: e.target.value })} /></Field>
              {!user && <Field label={`${i18n.field('email')} *`} error={errors.email}><Input type="email" autoComplete="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} /></Field>}
              {!user && <Field label={`${i18n.t('auth.password')} *`} hint={i18n.t('auth.password_rules')} error={errors.password}><Input type="password" autoComplete="new-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} /></Field>}
              <Field label={`${i18n.field('phone')} *`} error={errors.phone}><Input type="tel" autoComplete="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} /></Field>
              <Field label={i18n.field('date_of_birth')} error={errors.date_of_birth}><Input type="date" value={v.dateOfBirth} onChange={(e) => setV({ ...v, dateOfBirth: e.target.value })} /></Field>
              <Field label={i18n.field('street')}><Input autoComplete="address-line1" value={v.street} onChange={(e) => setV({ ...v, street: e.target.value })} /></Field>
              <Field label={i18n.field('house_number')}><Input value={v.house_number} onChange={(e) => setV({ ...v, house_number: e.target.value })} /></Field>
              <Field label={i18n.field('postal_code')}><Input autoComplete="postal-code" value={v.postal_code} onChange={(e) => setV({ ...v, postal_code: e.target.value })} /></Field>
              <Field label={i18n.field('city')}><Input autoComplete="address-level2" value={v.city} onChange={(e) => setV({ ...v, city: e.target.value })} /></Field>
            </div>
            <Checkbox checked={v.terms} onChange={(c) => setV({ ...v, terms: c })} label={<ConsentText />} />
            <Checkbox label={i18n.t('consent.marketing_email')} checked={v.marketingEmail} onChange={(c) => setV({ ...v, marketingEmail: c })} />
            <AsyncButton disabled={!v.terms || !v.firstName || !v.lastName || !v.phone || (!user && (!v.email || v.password.length < 10))} onClick={register}>{i18n.t('join.continue')}</AsyncButton>
          </Card>
        )}
        {plan && member && (
          <Card title={i18n.t('join.confirm_title')}>
            <p><strong>{plan.name}</strong> · <PlanPrice cents={plan.price_cents} interval={plan.billing_interval} count={plan.interval_count} /></p>
            {p.withdrawalEnabled && plan.billing_interval !== 'none' && (
              <Checkbox checked={early} onChange={setEarly} label={i18n.t('join.early_start', { days: p.withdrawalDays })} hint={i18n.t('join.early_start_hint', { days: p.withdrawalDays })} />
            )}
            <p className="small muted">{i18n.t('join.payment_info')}</p>
            <AsyncButton size="lg" disabled={!gym.gym.paymentsEnabled} onClick={checkout}>{i18n.t('join.pay')}</AsyncButton>
          </Card>
        )}
        {!user && <p className="small">{i18n.t('auth.have_account')} <Link to={`/login?gym=${gym.slug}&next=${encodeURIComponent(`${gym.base}/join${planId ? `?plan=${planId}` : ''}`)}`}>{i18n.t('auth.login')}</Link></p>}
      </div>
    </PublicFrame>
  );
}

export function WelcomePage() {
  const gym = useGym();
  const i18n = useI18n();
  const { query } = useLocation();
  const id = query.get('membership');
  const [status, setStatus] = useState<string>('pending');
  const [tries, setTries] = useState(0);
  const check = async (): Promise<void> => {
    try {
      const me = await api(`${T(gym.slug)}/me/memberships`);
      const m = me.find((x: { id: string }) => x.id === id);
      if (m) setStatus(m.status);
    } catch { /* keep waiting */ }
    setTries((n) => n + 1);
  };
  useEffect(() => { void check(); }, []);
  useInterval(check, status === 'active' || tries > 15 ? null : 2000);
  return (
    <PublicFrame>
      <div className="narrow stack center-text">
        {status === 'active' ? (
          <>
            <div className="success-mark" aria-hidden="true">✓</div>
            <h1>{i18n.t('welcome.title', { gym: gym.gym.name })}</h1>
            <p>{i18n.t('welcome.text')}</p>
            <Button size="lg" onClick={() => navigate(`${gym.base}/planner`)}>{i18n.t('welcome.book_first')}</Button>
            <p className="small muted">{i18n.t('welcome.app_hint')}</p>
          </>
        ) : tries > 15 ? (
          <p className="alert alert-warn">{i18n.t('welcome.slow')}</p>
        ) : (
          <><Loading /><p>{i18n.t('welcome.processing')}</p></>
        )}
      </div>
    </PublicFrame>
  );
}

export function NotMemberPage() {
  const gym = useGym();
  const i18n = useI18n();
  return (
    <PublicFrame>
      <div className="narrow stack center-text">
        <h1>{i18n.t('notmember.title', { gym: gym.gym.name })}</h1>
        <p className="muted">{i18n.t('notmember.text')}</p>
        <div className="row gap-sm center wrap">
          {gym.gym.policies.trial && <Button onClick={() => navigate(`${gym.base}/trial`)}>{i18n.t('public.book_trial')}</Button>}
          <Button variant="secondary" onClick={() => navigate(`${gym.base}/join`)}>{i18n.t('public.become_member')}</Button>
        </div>
      </div>
    </PublicFrame>
  );
}

